import {
  layerList,
  templateGet,
  templateGetLatestSeries,
  workspaceList,
  type ConnectMapsWebApiDtosEnumsGeoWorkspaceType,
  type ConnectMapsWebApiDtosResponsesGeoLayer,
  type ConnectMapsWebApiDtosResponsesGeoTemplate,
  type ConnectMapsWebApiDtosResponsesGeoWorkspace,
  type ConnectMapsWebApiDtosTemplateFieldModelBase,
} from '../generated';
import { type MapsClient } from '../wrappers/maps-service-client';

import {
  childGeospatialLogger,
  type GeospatialLogger,
  DEFAULT_LAYER_STYLE,
  ILayerService,
  LayerServiceSupport,
  LayerTemplate,
  MapLayer,
  MapWorkspace,
  MapWorkspaceType,
  PropertyDefinition,
  PropertyType,
  SystemLayerConfig,
} from '@trimble/geospatial';
import { UnreachableCaseError } from 'ts-essentials';

interface TemplateModelJson {
  name?: string | null;
  geometryType?: string | null;
  fields?: ConnectMapsWebApiDtosTemplateFieldModelBase[] | null;
}

interface ChoiceFieldValues {
  values?: Array<{ text?: string | null; default?: boolean | null }> | null;
}

const WORKSPACE_TYPE_BY_API: Record<
  ConnectMapsWebApiDtosEnumsGeoWorkspaceType,
  MapWorkspaceType
> = {
  GIS: MapWorkspaceType.Gis,
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Maps API workspace type literal
  Survey: MapWorkspaceType.Survey,
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Maps API workspace type literal
  FileViewer: MapWorkspaceType.FileViewer,
};

/** ILayerService backed by Maps Service API (host supplies the hey-api client). */
export class MapsApiLayerStore implements ILayerService {
  public readonly support: LayerServiceSupport = {
    supportsSchema: true,
  };

  private readonly logger: GeospatialLogger;

  constructor(
    private readonly client: MapsClient,
    logger: GeospatialLogger,
    private readonly systemLayerConfig: Pick<
      SystemLayerConfig,
      'isSystemLayerId'
    >
  ) {
    this.logger = childGeospatialLogger(logger, 'MapsApiLayerStore');
  }

  public async listWorkspaces(projectId: string): Promise<MapWorkspace[]> {
    this.logger.debug('listWorkspaces request', { projectId });
    const res = await workspaceList({
      client: this.client,
      path: { projectId },
      throwOnError: true,
    });
    const items = res.data?.items ?? [];
    this.logger.info('listWorkspaces response', { count: items.length });
    return items
      .map((item) => this.toMapWorkspace(item))
      .filter((item): item is MapWorkspace => item !== undefined);
  }

  public async listLayers(
    projectId: string,
    workspaceId?: string
  ): Promise<MapLayer[]> {
    this.logger.debug('listLayers request', { projectId, workspaceId });
    const query = workspaceId
      ? // eslint-disable-next-line @typescript-eslint/naming-convention -- Maps API query param is PascalCase
        { WorkspaceId: workspaceId }
      : undefined;
    const res = await layerList({
      client: this.client,
      path: { projectId },
      query,
      throwOnError: true,
    });
    const items = res.data?.items ?? [];
    this.logger.info('listLayers response', { count: items.length });
    return items
      .map((item) => this.toMapLayer(item, workspaceId))
      .filter((item): item is MapLayer => item !== undefined);
  }

  public async getLatestTemplate(
    projectId: string,
    layer: Pick<MapLayer, 'templateSeriesId' | 'templateId'>
  ): Promise<LayerTemplate | undefined> {
    if (layer.templateSeriesId) {
      this.logger.debug('getLatestTemplate (series) request', {
        projectId,
        seriesId: layer.templateSeriesId,
      });
      const res = await templateGetLatestSeries({
        client: this.client,
        path: { projectId, seriesId: layer.templateSeriesId },
        throwOnError: true,
      });
      return res.data ? this.toLayerTemplate(res.data) : undefined;
    }

    if (layer.templateId) {
      this.logger.debug('getLatestTemplate (id) request', {
        projectId,
        templateId: layer.templateId,
      });
      const res = await templateGet({
        client: this.client,
        path: { projectId, id: layer.templateId },
        query: { allInSeries: false },
        throwOnError: true,
      });
      return res.data ? this.toLayerTemplate(res.data) : undefined;
    }

    this.logger.debug('getLatestTemplate skipped; layer has no template', {
      projectId,
    });
    return undefined;
  }

  private toMapWorkspace(
    item: ConnectMapsWebApiDtosResponsesGeoWorkspace
  ): MapWorkspace | undefined {
    if (!item.id || !item.name) {
      this.logger.error('Skipping workspace missing id or name', { item });
      return undefined;
    }

    return {
      id: item.id,
      name: item.name,
      type: this.toWorkspaceType(item.type),
      layerIds: item.relationships?.layerIds ?? [],
    };
  }

  private toWorkspaceType(type: string | undefined): MapWorkspaceType {
    // The Maps API can add a type before this client knows it.
    // One unknown value must not fail the whole workspace list.
    if (!this.isSupportedWorkspaceType(type)) {
      if (type !== undefined) {
        this.logger.warn('Unknown map workspace type', { type });
      }
      return MapWorkspaceType.Unknown;
    }

    return WORKSPACE_TYPE_BY_API[type];
  }

  private isSupportedWorkspaceType(
    type: string | undefined
  ): type is ConnectMapsWebApiDtosEnumsGeoWorkspaceType {
    return type !== undefined && Object.hasOwn(WORKSPACE_TYPE_BY_API, type);
  }

  private toMapLayer(
    item: ConnectMapsWebApiDtosResponsesGeoLayer,
    workspaceId: string | undefined
  ): MapLayer | undefined {
    if (!item.id || !item.layerName) {
      this.logger.error('Skipping layer missing id or name', { item });
      return undefined;
    }

    return {
      id: item.id,
      name: item.layerName,
      workspaceId,
      templateId: item.template?.id ?? undefined,
      templateSeriesId: item.template?.seriesId ?? undefined,
      style: DEFAULT_LAYER_STYLE,
      isSystem: this.systemLayerConfig.isSystemLayerId(item.id),
    };
  }

  private toLayerTemplate(
    template: ConnectMapsWebApiDtosResponsesGeoTemplate
  ): LayerTemplate | undefined {
    if (!template.id) {
      this.logger.error('Skipping template missing id', { template });
      return undefined;
    }

    if (!template.template) {
      this.logger.error('Skipping template missing schema body', {
        templateId: template.id,
      });
      return undefined;
    }

    const model = template.template as TemplateModelJson;
    const fields = model.fields ?? [];
    const properties: PropertyDefinition[] = fields
      .map((field) => this.toPropertyDefinition(field))
      .filter((def): def is PropertyDefinition => def !== undefined);

    return {
      templateId: template.id,
      templateSeriesId: template.seriesId,
      version: template.version,
      name: model.name ?? undefined,
      geometryType: model.geometryType ?? undefined,
      properties,
    };
  }

  private toPropertyDefinition(
    field: ConnectMapsWebApiDtosTemplateFieldModelBase
  ): PropertyDefinition | undefined {
    const type = this.toPropertyType(field.type);
    if (type === undefined || !field.name) {
      return undefined;
    }

    const choiceValues =
      type === PropertyType.Choice
        ? ((field as ChoiceFieldValues).values ?? [])
        : [];

    return {
      name: field.name,
      displayName: field.displayName ?? field.name,
      type,
      required: field.required ?? false,
      readOnly: field.readOnly ?? false,
      choices: choiceValues
        .filter((value) => Boolean(value.text))
        .map((value) => ({ value: value.text ?? '', label: value.text ?? '' })),
    };
  }

  private toPropertyType(
    type: ConnectMapsWebApiDtosTemplateFieldModelBase['type']
  ): PropertyType | undefined {
    switch (type) {
      case 'Text':
      case 'Barcode':
        return PropertyType.Text;
      case 'Number':
        return PropertyType.Number;
      case 'YesNo':
        return PropertyType.Boolean;
      case 'Date':
        return PropertyType.Date;
      case 'Choice':
      case 'CodedChoice':
        return PropertyType.Choice;
      case 'Image':
      case 'Signature':
      case 'PageHeader':
      case 'Autofield':
      case 'CustomField':
      case 'SensorGroup':
      case undefined:
        return undefined;
      default:
        throw new UnreachableCaseError(type);
    }
  }
}
